/**
 * Globalna szukajka w Topbarze (Ctrl+K / Cmd+K).
 *
 * Wyszukuje rownolegle leady i oferty. Pokazuje wyniki w dropdownie z 2 grupami.
 * Klik w wynik → navigate do rekordu, dropdown sie zamyka.
 *
 * Skrot klawiszowy: Ctrl+K (Win/Linux), Cmd+K (Mac) — globalny, dziala
 * z dowolnego miejsca w aplikacji.
 *
 * RBAC + PII enforce'owany backendowo (handlowiec widzi tylko swoje).
 */

import { cn } from '@/lib/cn';
import { LEAD_STATUS_LABELS, type LeadStatus } from '@coreltb/shared/constants';
import type { SearchLeadHit, SearchOfferHit } from '@coreltb/shared/types';
import { FileText, Search, User, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useGlobalSearch } from './api';

export function GlobalSearch() {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce 250ms — uderzamy backend dopiero jak user przestal pisac
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 250);
    return () => clearTimeout(t);
  }, [query]);

  const { data, isFetching } = useGlobalSearch(debounced);

  // Skrot Ctrl+K / Cmd+K — focus input, otworz dropdown
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isCtrlK = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k';
      if (isCtrlK) {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
        setOpen(true);
      }
      if (e.key === 'Escape' && open) {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // Click outside → close
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const hasQuery = debounced.trim().length >= 2;
  const leadsHits = data?.leads ?? [];
  const offersHits = data?.offers ?? [];
  const totalHits = leadsHits.length + offersHits.length;

  const closeAndClear = () => {
    setOpen(false);
    setQuery('');
    setDebounced('');
  };

  return (
    <div ref={containerRef} className="relative hidden w-full max-w-md md:block">
      {/* Input */}
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-muted" />
      <input
        ref={inputRef}
        type="search"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Szukaj leadów, ofert…  Ctrl+K"
        className="form-input h-10 w-full rounded-md border border-border bg-surface-2 pl-9 pr-3 text-sm placeholder:text-fg-subtle transition-colors hover:border-border-strong focus:border-accent focus:bg-surface focus:ring-2 focus:ring-accent-ring"
      />

      {/* Dropdown */}
      {open && hasQuery && (
        <div className="absolute left-0 top-full z-40 mt-2 w-[32rem] max-w-[90vw] overflow-hidden rounded-lg border border-border bg-surface shadow-overlay">
          <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
            <h3 className="text-xs font-semibold text-fg-muted">
              {isFetching ? 'Szukam...' : `Wyniki (${totalHits})`}
            </h3>
            <button
              type="button"
              onClick={closeAndClear}
              className="rounded p-1 text-fg-muted hover:bg-surface-2 hover:text-fg"
              aria-label="Zamknij"
            >
              <X className="h-3.5 w-3.5" strokeWidth={2} />
            </button>
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {totalHits === 0 && !isFetching ? (
              <div className="py-8 text-center text-sm text-fg-muted">
                Brak wyników dla &laquo;{debounced}&raquo;.
                <div className="mt-2 text-xs text-fg-subtle">
                  Szukamy po: mieście, regionie, tagach, telefonie, e-mailu, numerze oferty.
                </div>
              </div>
            ) : (
              <>
                {leadsHits.length > 0 && (
                  <Group title="Leady" icon={User} count={leadsHits.length}>
                    {leadsHits.map((lead) => (
                      <LeadHit key={lead.id} lead={lead} onClose={closeAndClear} />
                    ))}
                  </Group>
                )}
                {offersHits.length > 0 && (
                  <Group title="Oferty" icon={FileText} count={offersHits.length}>
                    {offersHits.map((offer) => (
                      <OfferHit key={offer.id} offer={offer} onClose={closeAndClear} />
                    ))}
                  </Group>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Group({
  title,
  icon: Icon,
  count,
  children,
}: {
  title: string;
  icon: import('lucide-react').LucideIcon;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 border-b border-border bg-bg/40 px-4 py-2 text-xs font-semibold text-fg-muted">
        <Icon className="h-3 w-3 text-accent" strokeWidth={2.5} />
        {title}
        <span className="ml-auto text-fg-subtle">{count}</span>
      </div>
      <ul>{children}</ul>
    </div>
  );
}

function LeadHit({ lead, onClose }: { lead: SearchLeadHit; onClose: () => void }) {
  const statusLabel = LEAD_STATUS_LABELS[lead.status as LeadStatus] ?? lead.status;
  return (
    <li className="border-b border-border last:border-b-0">
      <Link
        to={`/crm/${lead.id}`}
        onClick={onClose}
        className="block px-4 py-2.5 transition-colors hover:bg-surface-2"
      >
        <div className="flex items-start justify-between gap-2">
          <span className="font-medium text-fg">{lead.name ?? '—'}</span>
          <span className="shrink-0 rounded border border-border bg-bg/60 px-1.5 py-0.5 text-xs text-fg-muted">
            {statusLabel}
          </span>
        </div>
        <div className="mt-0.5 truncate text-xs text-fg-muted">
          {lead.city}
          {lead.phone && ` · ${lead.phone}`}
        </div>
      </Link>
    </li>
  );
}

function OfferHit({ offer, onClose }: { offer: SearchOfferHit; onClose: () => void }) {
  return (
    <li className="border-b border-border last:border-b-0">
      <Link
        to={`/oferty/${offer.id}`}
        onClick={onClose}
        className="block px-4 py-2.5 transition-colors hover:bg-surface-2"
      >
        <div className="flex items-start justify-between gap-2">
          <span className="font-medium text-fg">
            {offer.offerNumberLabel ?? 'Oferta'}
            {offer.leadName && (
              <span className="ml-2 font-normal text-fg-muted">· {offer.leadName}</span>
            )}
          </span>
          <span
            className={cn(
              'shrink-0 rounded border px-1.5 py-0.5 text-xs',
              offer.status === 'accepted'
                ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                : offer.status === 'sent' || offer.status === 'opened' || offer.status === 'clicked'
                  ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-700 dark:text-cyan-300'
                  : 'border-border bg-bg/60 text-fg-muted',
            )}
          >
            {offer.status}
          </span>
        </div>
        <div className="mt-0.5 truncate text-xs text-fg-muted">
          {offer.leadCity} · {formatPln(offer.totalBruttoGrosze)} brutto
        </div>
      </Link>
    </li>
  );
}

function formatPln(grosze: number): string {
  return (grosze / 100).toLocaleString('pl-PL', {
    style: 'currency',
    currency: 'PLN',
    maximumFractionDigits: 0,
  });
}
